/**
 * 名字修改建议（基于《五行大义》补缺原则）
 *
 * 来源参考：
 *  - 隋·萧吉《五行大义》"辨体性""论数""论相生"三卷
 *  - 命学传统：五行"相生链""同类相助"
 *
 * 补缺逻辑：
 *  - 缺某行 → 推荐该行常见部首 / 代表字
 *  - 同时考虑相生：补一行也间接助其"生我"之行
 *  - 缺多行 → 按"主导行所喜"和"德性"双维度推荐
 */

import type { Element } from "../shared/types";

export interface SuggestionItem {
  /** 推荐使用的部首/字根 */
  radical: string;
  /** 元素 */
  element: Element;
  /** 简短理由 */
  reason: string;
  /** 例字（部首所属的字，可直接用作名） */
  examples: string[];
}

export interface NameAdvice {
  /** 主标题 */
  title: string;
  /** 引言（《五行大义》节选） */
  quote: string;
  /** 引言出处 */
  quoteSource: string;
  /** 缺失元素列表 */
  missing: Element[];
  /** 缺失对应建议 */
  suggestions: SuggestionItem[];
  /** 综合原则（针对整体） */
  generalTips: string[];
}

/** 五行相生：金→水→木→火→土→金 */
const SHENG: Record<Element, Element> = {
  金: "水", 水: "木", 木: "火", 火: "土", 土: "金",
};
/** 五行相生反推：谁生我 */
const SHENG_REV: Record<Element, Element> = {
  金: "土", 水: "金", 木: "水", 火: "木", 土: "火",
};

/** 每个元素常用字根 + 例字 */
const ELEMENT_RECOMMEND: Record<Element, { radicals: string[]; examples: string[] }> = {
  金: {
    radicals: ["金", "钅", "刂", "玉", "王", "刀"],
    examples: ["鑫", "钰", "铭", "锋", "锐", "钦", "钧", "镜", "钊", "银"],
  },
  木: {
    radicals: ["木", "艹", "禾", "竹", "林"],
    examples: ["森", "林", "松", "柏", "桐", "萱", "莹", "芮", "颖", "苇"],
  },
  水: {
    radicals: ["水", "氵", "冫", "雨", "泉"],
    examples: ["泽", "海", "涵", "潇", "汐", "澜", "溪", "雨", "雪", "冰"],
  },
  火: {
    radicals: ["火", "灬", "日", "光", "炎"],
    examples: ["炎", "焱", "煜", "煊", "昭", "明", "晖", "晗", "旭", "昕"],
  },
  土: {
    radicals: ["土", "士", "山", "石", "城"],
    examples: ["坤", "城", "岩", "峰", "岚", "岱", "坤", "坦", "堂", "堃"],
  },
};

/** 五行特质 → 简短补缺评语 */
const ELEMENT_DESC: Record<Element, string> = {
  金: "金主义，肃杀成器；缺金则少果断权衡之力",
  木: "木主仁，曲直可绳；缺木则失生发担当之气",
  水: "水主智，润下以顺；缺水则机敏深邃不显",
  火: "火主礼，炎上以明；缺火则热情感染难彰",
  土: "土主信，稼穑以成；缺土则厚重承载无根",
};

/** 《五行大义》引言（按卷） */
const QUOTES: Array<{ text: string; source: string }> = [
  {
    text: "五行皆禀于一气，分而为五，互相损益，以成万事。",
    source: "《五行大义·释名》卷一",
  },
  {
    text: "木生火，火生土，土生金，金生水，水生木，是谓相生之序。",
    source: "《五行大义·论相生》卷三",
  },
  {
    text: "五物之生，皆因气母；气偏则身弱，气备则命昌。",
    source: "《五行大义·辨体性》卷二",
  },
  {
    text: "行者，天地之经，五常之纬；偏阙则病，俱全则祥。",
    source: "《五行大义·论德》卷四",
  },
];

/**
 * 生成修改建议
 * @param missing 当前缺失的元素（counts 为 0）
 * @param dominant 当前主导元素
 */
export function buildNameAdvice(
  missing: Element[],
  dominant: Element
): NameAdvice {
  // 引言：按缺的数量选不同引文
  let quoteIdx: number;
  if (missing.length === 0) quoteIdx = 0;
  else if (missing.length <= 2) quoteIdx = 1;
  else if (missing.length <= 3) quoteIdx = 2;
  else quoteIdx = 3;
  const q = QUOTES[quoteIdx];

  // 每个缺失元素给具体建议
  const suggestions: SuggestionItem[] = missing.map((el) => {
    const rec = ELEMENT_RECOMMEND[el];
    const desc = ELEMENT_DESC[el];
    // 相生链上的"生我者"也建议（因为补一行有连锁效应）
    const helper = SHENG_REV[el];
    const helperRec = ELEMENT_RECOMMEND[helper];
    const helperHint = helper === dominant
      ? `且${helper}为你主导行（${ELEMENT_DESC[helper]}），可顺势加强`
      : `辅以${helper}之字（${ELEMENT_DESC[helper].slice(0, 12)}）相生`;

    return {
      radical: rec.radicals.join(" / "),
      element: el,
      reason: `${desc}。${helperHint}。`,
      examples: rec.examples.slice(0, 6),
    };
  });

  // 综合原则
  const generalTips: string[] = [];
  if (missing.length === 0) {
    generalTips.push("五行俱全，气运平衡；保持即可，无需大改。");
    generalTips.push("若想更上一层楼，可在名中加强主导行的「生我」之行，固本培元。");
  } else if (missing.length === 1) {
    generalTips.push("仅缺一行，补一字即可周全；推荐加入上表所列字根。");
    generalTips.push("注意：避免与已有字相克（如金盛再添金克木）。");
  } else if (missing.length === 2) {
    generalTips.push("缺两行，可优先补主导行「所喜」之行（《五行大义》所谓『母气得助』）。");
    generalTips.push("若名字仅 2 字，建议将第二字改为同时含两行部首的字（如「润」兼水木）。");
  } else {
    generalTips.push("缺三行以上，五行偏枯；宜用一字带多行的「复合部首」字。");
    generalTips.push("复合字示例：「森」(木木)、「鑫」(金金金)、「灏」(水水)、「焱」(火火)、「垚」(土土土)。");
    generalTips.push("或取自五行兼具之字：「润」(氵+王 = 水+金)、「清」(氵+青 = 水+木)。");
  }

  return {
    title: "五行修改建议",
    quote: q.text,
    quoteSource: q.source,
    missing,
    suggestions,
    generalTips,
  };
}